import { Component, EventEmitter, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AppService } from '@app/app.service';
import { CustomDatePickerAdapter, CUSTOM_DATE_FORMATS } from '@app/core/date-adapter';
import { getLanguage } from '@app/core/material.module';
import { FuiDestroyService } from '@services/destroy.service';
import * as moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { FieldConfig, ITrailingButtonEvent } from '../field.interface';

@Component({
    selector: 'app-date',
    templateUrl: './date.component.html',
    styleUrls: ['../input/input.scss', '../date/date.component.scss'],
    providers: [
        FuiDestroyService,
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, deps: [AppService], useFactory: getLanguage },
        { provide: DateAdapter, useClass: CustomDatePickerAdapter, deps: [MAT_DATE_LOCALE] },
    ],
})
export class DateComponent implements OnInit {
    readonly #destroy$: FuiDestroyService = inject(FuiDestroyService);
    readonly appService = inject(AppService);
    field: FieldConfig;
    group: FormGroup;
    data: any;
    max = null;
    min = null;

    /** Событие TrailingButton */
    outputEvent: EventEmitter<ITrailingButtonEvent> = null;

    dataFilter = (d: Date): boolean => {
        if (!this.max && !this.min) {
            return true;
        }
        if (!this.max && this.min) {
            return this.min <= d;
        }
        if (this.max && !this.min) {
            return this.max >= d;
        }
        if (this.max && this.min) {
            return this.max >= d >= this.min;
        }
    };

    ngOnInit() {
        this.getMax();
        this.getMin();
        if (this.data) {
            if (this.data.maxField && this.group.get(this.data.maxField)) {
                this.group
                    .get(this.data.maxField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMax();
                        this.group.get(this.field.name).markAsTouched();
                        this.group.get(this.field.name).updateValueAndValidity({ emitEvent: false });
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.maxField).markAsTouched();
                        this.group.get(this.data.maxField).updateValueAndValidity({ emitEvent: false });
                    });
            }
            if (this.data.minField && this.group.get(this.data.minField)) {
                this.group
                    .get(this.data.minField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMin();
                        this.group.get(this.field.name).markAsTouched();
                        this.group.get(this.field.name).updateValueAndValidity({ emitEvent: false });
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.minField).markAsTouched();
                        this.group.get(this.data.minField).updateValueAndValidity({ emitEvent: false });
                    });
            }
        }

        if (this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
        if (this.field?.disabled) {
            this.setDisabled(true);
        }
    }

    /**
     * Получение максимального ограничения
     */
    getMax() {
        this.max = null;
        if (this.data && this.data.maxDate) {
            this.max = this.data.maxDate;
        } else if (this.data && this.data.maxField && this.group.get(this.data.maxField)) {
            const value = this.group.get(this.data.maxField).value ?? '';
            const mValue = moment(value);
            if (mValue.isValid()) {
                this.max = moment(this.group.get(this.data.maxField).value);
            }
        }
    }

    /**
     * Получение минимального ограничения
     */
    getMin() {
        this.min = null;
        if (this.data && this.data.minDate) {
            this.min = this.data.minDate;
        } else if (this.data && this.data.minField && this.group.get(this.data.minField)) {
            const value = this.group.get(this.data.minField).value ?? '';
            const mValue = moment(value);
            if (mValue.isValid()) {
                this.min = moment(this.group.get(this.data.minField).value);
            }
        }
    }

    /**
     * Изменение даты
     */
    changeEvent($event: ClipboardEvent) {
        let ctrlValue = this.group?.get(this.field.name)?.value ? this.group.get(this.field.name).value : null;
        if ($event) {
            ctrlValue = $event?.clipboardData?.getData('text');
        }
        if (typeof ctrlValue === 'string') {
            ctrlValue = moment(ctrlValue, 'DD.MM.YYYY')
                .hour(12)
                .minute(0)
                .second(0)
                .add(moment().utcOffset(), 'm')
                .utc(false);
        }
        if (ctrlValue) {
            if (this.field.name === 'to') {
                this.group
                    ?.get(this.field.name)
                    ?.setValue(
                        moment([ctrlValue.year(), ctrlValue.month(), ctrlValue.date()])
                            .add(0, 'hours')
                            .add(moment().utcOffset(), 'm')
                            .utc(false),
                    );
            } else if (this.field.name === 'from') {
                this.group
                    ?.get(this.field.name)
                    ?.setValue(
                        moment([ctrlValue.year(), ctrlValue.month(), ctrlValue.date()])
                            .add(0, 'hours')
                            .add(moment().utcOffset(), 'm')
                            .utc(false),
                    );
            } else {
                this.group
                    ?.get(this.field.name)
                    ?.setValue(
                        moment([ctrlValue.year(), ctrlValue.month(), ctrlValue.date()])
                            .add(12, 'hours')
                            .add(moment().utcOffset(), 'm')
                            .utc(false),
                    );
            }
        } else {
            this.group?.get(this.field.name)?.setValue(null);
        }
    }

    /**
     * Установка запрета изменения поля
     * @param disabled значение флага
     */
    setDisabled(disabled: boolean) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Нажатие на кнопку очистки поля
     */
    clear() {
        this.group.get(this.field.name)?.setValue(null);
        this.group.get(this.field.name)?.markAsDirty();
    }
}
